import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, AlertTriangle, MoreVertical, Trash2, Users } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CandidatesList } from './CandidatesList';

interface ServiceRequest {
  id: string;
  title: string;
  description: string;
  category: string;
  location: string;
  createdAt: any;
  status: 'open' | 'in_progress' | 'completed';
  urgency: 'low' | 'medium' | 'high';
  authorId: string;
}

interface ServiceCardProps {
  service: ServiceRequest;
  currentUserId: string;
  onApply: (id: string) => void;
  onDelete: (id: string) => void;
}

export function ServiceCard({ service, currentUserId, onApply, onDelete }: ServiceCardProps) {
  const [showCandidates, setShowCandidates] = useState(false);
  const isAuthor = currentUserId === service.authorId;

  const getTimeAgo = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp?.toDate?.() || new Date(timestamp);
    return formatDistanceToNow(date, {
      locale: ptBR,
      addSuffix: true
    });
  };

  const isOld = () => {
    if (!service.createdAt) return false;
    const date = service.createdAt?.toDate?.() || new Date(service.createdAt);
    return Date.now() - date.getTime() > 7 * 24 * 60 * 60 * 1000;
  };

  return (
    <>
      <Card className={`p-6 ${isOld() ? 'border-yellow-400 border-2' : ''}`}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">{service.title}</h3>
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
              <MapPin className="h-4 w-4" />
              <span>{service.location}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="h-4 w-4" />
              <span>{getTimeAgo(service.createdAt)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge 
              variant={service.urgency === 'high' ? 'destructive' : 'secondary'}
              className="capitalize"
            >
              {service.urgency === 'high' && <AlertTriangle className="h-4 w-4 mr-1" />}
              {service.urgency === 'high' ? 'Urgente' : service.urgency === 'medium' ? 'Média' : 'Baixa'}
            </Badge>
            {isAuthor && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShowCandidates(true)}>
                    <Users className="mr-2 h-4 w-4" />
                    <span>Ver Candidatos</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDelete(service.id)} className="text-red-600">
                    <Trash2 className="mr-2 h-4 w-4" />
                    <span>Excluir Serviço</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        <p className="text-gray-700 mb-4">{service.description}</p>

        <div className="flex items-center justify-between">
          <Badge variant="outline" className="capitalize">
            {service.category}
          </Badge>
          {!isAuthor && (
            <Button onClick={() => onApply(service.id)}>
              Candidatar-me
            </Button>
          )}
        </div>
      </Card>

      <Dialog open={showCandidates} onOpenChange={setShowCandidates}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Candidatos para o serviço</DialogTitle>
          </DialogHeader>
          <CandidatesList serviceId={service.id} />
        </DialogContent>
      </Dialog>
    </>
  );
}